import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppLogo } from '@/components/layout/AppLogo';
import { motion } from 'framer-motion';
import { Clock3, RefreshCcw, ShieldAlert, LayoutDashboard, LogOut, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { CyberBackground } from '@/components/landing/CyberBackground';
import {
  onboardingService,
  type AccessStatusResponse,
} from '@/services/onboardingService';

const ONBOARDING_PENDING_KEY = 'kibo:onboarding-pending';

function statusMessage(status: AccessStatusResponse | null): string {
  if (!status) {
    return 'Checking organization status...';
  }

  if (status.state === 'suspended') {
    return 'Your organization is currently suspended. Please contact support for assistance.';
  }

  if (status.requiresReauthentication) {
    return 'Approval was granted. Refresh your session to continue to your dashboard.';
  }

  return 'Your organization request is pending approval. We will grant dashboard access as soon as it is approved.';
}

export default function WaitingForApprovalPage() {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();

  const [status, setStatus] = useState<AccessStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  useEffect(() => {
    if (initialized && !keycloak.authenticated) {
      setIsLoading(false);
    }
  }, [initialized, keycloak.authenticated]);

  const refreshStatus = useCallback(async () => {
    if (!initialized || !keycloak.authenticated) {
      setIsLoading(false);
      return;
    }
    try {
      const result = await onboardingService.getAccessStatus();
      setStatus(result);

      if (result.state === 'approved') {
        window.localStorage.removeItem(ONBOARDING_PENDING_KEY);
      }

      if (result.state === 'approved' && !result.requiresReauthentication) {
        // Land on the role-appropriate home, not the asset dashboard.
        navigate(ROUTES.HOME, { replace: true });
        return;
      }

      if (result.state === 'rejected') {
        navigate(ROUTES.REJECTED_ORGANIZATION, { replace: true });
        return;
      }

      if (result.state === 'no_organization') {
        window.localStorage.removeItem(ONBOARDING_PENDING_KEY);
        navigate(ROUTES.NO_ORGANIZATION, { replace: true });
      }
    } catch {
      const hasPendingOnboarding =
        window.localStorage.getItem(ONBOARDING_PENDING_KEY) === '1';
      if (hasPendingOnboarding) {
        setStatus({
          state: 'pending_approval',
          isPlatformAdmin: false,
          organization: null,
          requiresReauthentication: false,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, initialized, keycloak.authenticated]);

  useEffect(() => {
    if (!initialized || !keycloak.authenticated) {
      return;
    }

    void refreshStatus();

    const timer = window.setInterval(() => {
      void refreshStatus();
    }, 15000);

    return () => window.clearInterval(timer);
  }, [initialized, keycloak.authenticated, refreshStatus]);

  const refreshSession = async () => {
    setIsRefreshingSession(true);
    try {
      await keycloak.login({
        redirectUri: `${window.location.origin}${ROUTES.HOME}`,
      });
    } finally {
      setIsRefreshingSession(false);
    }
  };

  const logout = async () => {
    await keycloak.logout({
      // No in-app login page — return to landing; Sign in button takes them
      // back into Keycloak if they want.
      redirectUri: `${window.location.origin}/`,
    });
  };

  const isSuspended = status?.state === 'suspended';
  const isApproved = status?.requiresReauthentication;

  return (
    <div className="h-screen flex flex-col bg-transparent overflow-hidden relative z-0">
      <CyberBackground />
      {/* Top Bar */}
      <header className="fixed top-0 w-full z-50 flex justify-between items-center px-8 h-20 bg-card/30 backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <AppLogo variant="full" alt="BlueB" className="h-10 w-auto" />
          <div className="h-6 w-px bg-border/50 mx-2 hidden sm:block"></div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-foreground">Organization Registration</h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => void logout()}
            className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 pt-20 items-center justify-center overflow-y-auto">
        <div className="flex flex-col items-center justify-center text-center py-20 px-4 max-w-2xl mx-auto">
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12, stiffness: 100 }}
            className={`w-24 h-24 rounded-full flex items-center justify-center mb-8 ${
              isSuspended
                ? 'bg-destructive/10 text-destructive'
                : isApproved
                ? 'bg-primary/10 text-primary'
                : 'bg-amber-500/10 text-amber-600'
            }`}
          >
            {isSuspended ? (
              <ShieldAlert className="w-12 h-12" />
            ) : isApproved ? (
              <CheckCircle2 className="w-12 h-12" />
            ) : (
              <Clock3 className="w-12 h-12" />
            )}
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {isSuspended ? 'Access Suspended' : isApproved ? 'Approved' : 'Pending Approval'}
            </p>
            <h2 className="text-4xl font-bold mb-4 text-foreground">
              {isSuspended ? 'Organization Suspended' : isApproved ? 'Approval Granted' : 'Waiting For Approval'}
            </h2>
          </motion.div>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground max-w-md mx-auto mb-10 leading-relaxed"
          >
            {statusMessage(status)}
          </motion.p>

          {/* Status Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-full max-w-md bg-card rounded-2xl border border-border shadow-soft p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-foreground">Status</span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isSuspended
                    ? 'bg-destructive/10 text-destructive'
                    : isApproved
                    ? 'bg-primary/10 text-primary'
                    : 'bg-amber-500/10 text-amber-600'
                }`}
              >
                {isSuspended ? 'Suspended' : isApproved ? 'Approved' : 'Pending'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {isSuspended
                ? 'Contact support for more information about your suspension.'
                : isApproved
                ? 'Your organization has been approved. Refresh your session to access the dashboard.'
                : 'We typically review applications within 1-2 business days. You will receive an email notification once your organization is approved.'}
            </p>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 w-full max-w-md"
          >
            <button
              type="button"
              onClick={() => void refreshStatus()}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-border bg-card text-foreground font-semibold hover:bg-muted transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Checking...' : 'Check Status'}
            </button>

            {status?.requiresReauthentication && (
              <button
                type="button"
                onClick={() => void refreshSession()}
                disabled={isRefreshingSession}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-all glow-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefreshingSession ? 'Refreshing...' : 'Refresh Session'}
              </button>
            )}
          </motion.div>

          {/* Auto-refresh indicator */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-xs text-muted-foreground mt-6"
          >
            Status automatically refreshes every 15 seconds
          </motion.p>
        </div>
      </div>
    </div>
  );
}
